import { gql } from '@apollo/client';
import { apolloClient } from '../config/apollo-client';

// GraphQL mutation for adding game assignment
export const ADD_GAME_ASSIGNMENT_MUTATION = gql`
  mutation AddGameAssignment($input: AddGameToLabelInput!) {
    addGameAssignment(input: $input) {
      id
      name
      gameId
      providerId
      categoryId
      launchUrl
      demoAvailable
      freespinAvailable
      deviceType
      betType
      linesCount
      isActive
      iconUrl
      description
      createdAt
      position
    }
  }
`;

// Types for the add game assignment input
export interface AddGameToLabelInput {
  gameId: number;
  environmentId: number;
  labelId: number;
  position: number;
}

// Raw GraphQL response interface (before transformation)
export interface RawGame {
  id: string; // GraphQL ID fields are strings
  name: string;
  gameId: string;
  providerId: number;
  categoryId: number;
  launchUrl?: string | null;
  demoAvailable: boolean;
  freespinAvailable: boolean;
  deviceType: string;
  betType?: string | null;
  linesCount?: number | null;
  isActive: boolean;
  iconUrl?: string | null;
  description?: string | null;
  createdAt: string;
  position: number;
}

// Transformed Game interface (after conversion)
export interface Game {
  id: number;
  name: string;
  gameId: string;
  providerId: number;
  categoryId: number;
  launchUrl?: string | null;
  demoAvailable: boolean;
  freespinAvailable: boolean;
  deviceType: string;
  betType?: string | null;
  linesCount?: number | null;
  isActive: boolean;
  iconUrl?: string | null;
  description?: string | null;
  createdAt: string;
  position: number;
}

export interface AddGameAssignmentResponse {
  addGameAssignment: RawGame;
}

// AddGameAssignment service class
export class AddGameAssignmentService {
  private static instance: AddGameAssignmentService;

  private constructor() {}

  public static getInstance(): AddGameAssignmentService {
    if (!AddGameAssignmentService.instance) {
      AddGameAssignmentService.instance = new AddGameAssignmentService();
    }
    return AddGameAssignmentService.instance;
  }

  // Add game assignment
  async addGameAssignment(input: AddGameToLabelInput): Promise<Game> {
    try {
      const { data } = await apolloClient.mutate<AddGameAssignmentResponse>({
        mutation: ADD_GAME_ASSIGNMENT_MUTATION,
        variables: { input },
      });

      if (!data?.addGameAssignment) {
        throw new Error('No data returned from add game assignment mutation');
      }

      // Transform the response to convert string ID to number
      const transformedGame = {
        ...data.addGameAssignment,
        id: Number(data.addGameAssignment.id),
      };

      return transformedGame;
    } catch (error: any) {
      console.error('Error adding game assignment:', error);
      if (error.graphQLErrors) {
        error.graphQLErrors.forEach((err: any) => {
          console.error('GraphQL Error:', err.message);
          console.error('Locations:', err.locations);
          console.error('Path:', err.path);
        });
      }
      if (error.networkError) {
        console.error('Network Error:', error.networkError);
      }
      throw error;
    }
  }
}

// Export singleton instance
export const addGameAssignmentService = AddGameAssignmentService.getInstance();

// Export convenience function
export async function addGameAssignment(
  input: AddGameToLabelInput,
): Promise<Game> {
  return addGameAssignmentService.addGameAssignment(input);
}
